import React, { useState } from "react";
import LinkGeneratedIcon from "../../ui/icons/LinkGeneratedIcon";
import CopyIcon from "../../ui/icons/CopyIcon";
import StandardButton from "../../ui/Button/StandardButton";

interface LinkGeneratedDialogProps {
  jobId: string;
  candidateName: string;
  email: string;
  id: string;
  interviewUrl: string; // Now passed from API response
  onClose?: () => void;
  onCopyLink?: () => void;
}

const LinkGeneratedDialog: React.FC<LinkGeneratedDialogProps> = ({
  jobId,
  candidateName,
  email,
  id,
  interviewUrl,
  onClose,
  onCopyLink,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(interviewUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    onCopyLink?.();
  };

  const labelStyle: React.CSSProperties = {
    color: "var(--color-beige, #63615C)",
    fontFamily: "Interstate",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "normal",
  };

  const valueStyle: React.CSSProperties = {
    color: "var(--color-charcoal, #63615C)",
    fontFamily: "Interstate",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "normal",
    marginTop: "4px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  return (
    <div>
      {/* Row 1: Icon */}
      <div style={{ marginBottom: "10px" }}>
        <LinkGeneratedIcon />
      </div>

      {/* Row 2: Title */}
      <div
        style={{
          color: "var(--color-charcoal, #63615C)",
          fontFamily: "Interstate",
          fontSize: "20px",
          fontStyle: "normal",
          fontWeight: 700,
          lineHeight: "normal",
          marginBottom: "4px",
        }}
      >
        Link Generated
      </div>

      {/* Row 3: Description */}
      <div
        style={{
          color: "rgba(99, 97, 92, 0.60)",
          fontFamily: "Interstate",
          fontSize: "14px",
          fontStyle: "normal",
          fontWeight: 400,
          lineHeight: "20px",
          marginBottom: "16px",
        }}
      >
        <div>New interview link has been generated successfully!</div>
        <div>This link will be valid for 5 calendar days.</div>
      </div>

      {/* Row 4: Details Table */}
      <div className="link-generated-details">
        {/* Job ID */}
        <div className="link-generated-field">
          <div style={labelStyle}>Job ID</div>
          <div style={valueStyle} title={jobId}>{jobId}</div>
        </div>

        {/* Candidate Name */}
        <div className="link-generated-field">
          <div style={labelStyle}>Candidate Name</div>
          <div style={valueStyle} title={candidateName}>{candidateName}</div>
        </div>

        {/* Email Address */}
        <div className="link-generated-field">
          <div style={labelStyle}>Email Address</div>
          <div style={valueStyle} title={email}>{email}</div>
        </div>
      </div>

      {/* Row 5: Link and Copy Button */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
        }}
      >
        <div
          style={{
            flex: 1,
            borderRadius: "8px",
            border: "1px solid #E5E7EB",
            background: "#F9FAFB",
            height: "36px",
            padding: "5px 8px 5px 13px",
            display: "flex",
            alignItems: "center",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          <a
            href={interviewUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "var(--color-charcoal, #63615C)",
              fontFamily: "Interstate",
              fontSize: "14px",
              fontWeight: 400,
              textDecoration: "none",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {interviewUrl}
          </a>
        </div>
        <StandardButton
          text={copied ? "Copied!" : "Copy Link"}
          startIcon={copied ? undefined : <CopyIcon />}
          variant="outline"
          color="secondary"
          size="small"
          onClick={handleCopyLink}
        />
      </div>
    </div>
  );
};

export default LinkGeneratedDialog;
